import {
  Armchair,
  Beer,
  BedSingle,
  Bike,
  Bus,
  Coffee,
  Droplet,
  Eye,
  Footprints,
  Info,
  Landmark,
  Mountain,
  Palette,
  Plane,
  Ship,
  ShoppingBasket,
  ShoppingCart,
  SquareParking,
  Tent,
  TentTree,
  Trees,
  TrainFront,
  TreePine,
  Umbrella,
  Utensils,
  Wrench,
} from '@lucide/vue'

import type { Component } from 'vue'

/**
 * gpx.studio / Komoot-style POI category browser, powered entirely by the POIs already baked into
 * the route.camp vector basemap (the `pois_*` style layers in `mapStore`'s `pointOfInterestLayers`).
 * No Overpass / external API — turning a category on draws its POIs (queried from the basemap vector
 * source by `kind`) as RouteCamp markers, so the whole feature works offline with downloaded
 * collections.
 *
 * Each category maps to:
 * - one or more existing `pois_*` layer ids (whose suffix is the vector-tile `kind`);
 * - a `poiType` — one of the RouteCamp workspace-POI types consumed by `getPOIIcon` / `getPoiColor` —
 *   assigned when a basemap POI is added to a collection (drives marker icon + colour);
 * - `gpxSym` / `gpxType` — the GPX `<sym>` / `<type>` to emit on export for cross-platform
 *   portability. `<sym>` values are the exact Garmin / gpx.studio Title-Case names (the de-facto
 *   standard that gpx.studio + Garmin BaseCamp render and round-trip). See
 *   `docs/map-overlays-and-pois.md` for the compatibility notes.
 */
export interface PoiBrowseCategory {
  /** Stable id (used for keys + persisted UI state). */
  id: string
  /** Human label shown in the checklist. */
  label: string
  /** Checklist icon. */
  icon: Component
  /** Existing basemap `pois_*` layer ids this category toggles. */
  layerIds: string[]
  /** RouteCamp workspace-POI type assigned when adding one of these POIs to a collection. */
  poiType: string
  /** GPX `<sym>` for export (exact Garmin / gpx.studio spelling). */
  gpxSym: string
  /** GPX `<type>` classification for export (lowercase, loosely aligned to RideWithGPS ids). */
  gpxType: string
}

export interface PoiBrowseGroup {
  id: string
  label: string
  categories: PoiBrowseCategory[]
}

export const POI_BROWSE_GROUPS: PoiBrowseGroup[] = [
  {
    id: 'food',
    label: 'Food & drink',
    categories: [
      {
        id: 'restaurant',
        label: 'Restaurants',
        icon: Utensils,
        layerIds: ['pois_restaurant'],
        poiType: 'food',
        gpxSym: 'Restaurant',
        gpxType: 'food',
      },
      {
        id: 'fast_food',
        label: 'Fast food',
        icon: Utensils,
        layerIds: ['pois_fast_food'],
        poiType: 'food',
        gpxSym: 'Fast Food',
        gpxType: 'food',
      },
      {
        id: 'cafe',
        label: 'Cafés',
        icon: Coffee,
        layerIds: ['pois_cafe'],
        poiType: 'food',
        gpxSym: 'Restaurant',
        gpxType: 'coffee',
      },
      {
        id: 'bar',
        label: 'Bars & pubs',
        icon: Beer,
        layerIds: ['pois_bar'],
        poiType: 'food',
        gpxSym: 'Bar',
        gpxType: 'bar',
      },
    ],
  },
  {
    id: 'accommodation',
    label: 'Accommodation',
    categories: [
      {
        id: 'lodging',
        label: 'Hotels & lodging',
        icon: BedSingle,
        layerIds: ['pois_hotel', 'pois_motel', 'pois_hostel', 'pois_guest_house', 'pois_lodging'],
        poiType: 'accommodation',
        gpxSym: 'Lodging',
        gpxType: 'lodging',
      },
    ],
  },
  {
    id: 'shopping',
    label: 'Shopping',
    categories: [
      {
        id: 'supermarket',
        label: 'Supermarkets',
        icon: ShoppingCart,
        layerIds: ['pois_supermarket'],
        poiType: 'shopping',
        gpxSym: 'Shopping Center',
        gpxType: 'shopping',
      },
      {
        id: 'convenience',
        label: 'Convenience stores',
        icon: ShoppingBasket,
        layerIds: ['pois_convenience'],
        poiType: 'shopping',
        gpxSym: 'Convenience Store',
        gpxType: 'convenience_store',
      },
    ],
  },
  {
    id: 'water',
    label: 'Water',
    categories: [
      {
        id: 'drinking_water',
        label: 'Drinking water',
        icon: Droplet,
        layerIds: ['pois_drinking_water', 'pois_water_point', 'pois_watering_place'],
        poiType: 'water',
        gpxSym: 'Drinking Water',
        gpxType: 'water',
      },
      {
        id: 'spring',
        label: 'Springs & fountains',
        icon: Droplet,
        layerIds: ['pois_spring', 'pois_fountain'],
        poiType: 'water',
        gpxSym: 'Water Source',
        gpxType: 'water',
      },
    ],
  },
  {
    id: 'mountains',
    label: 'Mountains & huts',
    categories: [
      {
        id: 'peak',
        label: 'Peaks',
        icon: Mountain,
        layerIds: ['pois_peak'],
        poiType: 'summit',
        gpxSym: 'Summit',
        gpxType: 'summit',
      },
      {
        id: 'alpine_hut',
        label: 'Alpine huts',
        icon: TentTree,
        layerIds: ['pois_alpine_hut'],
        poiType: 'shelter',
        gpxSym: 'Lodge',
        gpxType: 'lodging',
      },
      {
        id: 'wilderness_hut',
        label: 'Wilderness huts',
        icon: TentTree,
        layerIds: ['pois_wilderness_hut'],
        poiType: 'shelter',
        gpxSym: 'Lodge',
        gpxType: 'lodging',
      },
      {
        id: 'shelter',
        label: 'Shelters',
        icon: Footprints,
        layerIds: ['pois_shelter'],
        poiType: 'shelter',
        gpxSym: 'Shelter',
        gpxType: 'shelter',
      },
    ],
  },
  {
    id: 'tourism',
    label: 'Tourism',
    categories: [
      {
        id: 'attraction',
        label: 'Attractions',
        icon: Landmark,
        layerIds: ['pois_attraction'],
        poiType: 'landmark',
        gpxSym: 'Scenic Area',
        gpxType: 'attraction',
      },
      {
        id: 'viewpoint',
        label: 'Viewpoints',
        icon: Eye,
        layerIds: ['pois_viewpoint'],
        poiType: 'landmark',
        gpxSym: 'Scenic Area',
        gpxType: 'viewpoint',
      },
      {
        id: 'museum',
        label: 'Museums',
        icon: Landmark,
        layerIds: ['pois_museum'],
        poiType: 'landmark',
        gpxSym: 'Museum',
        gpxType: 'museum',
      },
      {
        id: 'artwork',
        label: 'Artwork',
        icon: Palette,
        layerIds: ['pois_artwork'],
        poiType: 'landmark',
        gpxSym: 'Scenic Area',
        gpxType: 'attraction',
      },
    ],
  },
  {
    id: 'outdoors',
    label: 'Outdoors',
    categories: [
      {
        id: 'camp_site',
        label: 'Campsites',
        icon: Tent,
        layerIds: ['pois_camp_site'],
        poiType: 'accommodation',
        gpxSym: 'Campground',
        gpxType: 'camping',
      },
      {
        id: 'picnic',
        label: 'Picnic spots',
        icon: Armchair,
        layerIds: ['pois_picnic_site', 'pois_picnic_table'],
        poiType: 'generic',
        gpxSym: 'Picnic Area',
        gpxType: 'picnic',
      },
      {
        id: 'park',
        label: 'Parks',
        icon: Trees,
        layerIds: ['pois_park'],
        poiType: 'forest',
        gpxSym: 'Park',
        gpxType: 'park',
      },
      {
        id: 'forest',
        label: 'Forests',
        icon: TreePine,
        layerIds: ['pois_forest'],
        poiType: 'forest',
        gpxSym: 'Forest',
        gpxType: 'forest',
      },
      {
        id: 'beach',
        label: 'Beaches',
        icon: Umbrella,
        layerIds: ['pois_beach'],
        poiType: 'landmark',
        gpxSym: 'Beach',
        gpxType: 'swimming',
      },
    ],
  },
  {
    id: 'amenities',
    label: 'Amenities',
    categories: [
      {
        id: 'toilets',
        label: 'Toilets',
        icon: Info,
        layerIds: ['pois_toilets'],
        poiType: 'generic',
        gpxSym: 'Restroom',
        gpxType: 'restroom',
      },
      {
        id: 'information',
        label: 'Information',
        icon: Info,
        layerIds: ['pois_information', 'pois_trailhead'],
        poiType: 'information',
        gpxSym: 'Information',
        gpxType: 'information',
      },
      {
        id: 'parking',
        label: 'Parking',
        icon: SquareParking,
        layerIds: ['pois_parking'],
        poiType: 'parking',
        gpxSym: 'Parking Area',
        gpxType: 'parking',
      },
    ],
  },
  {
    id: 'bicycle',
    label: 'Bicycle',
    categories: [
      {
        id: 'bicycle_parking',
        label: 'Bicycle parking',
        icon: Bike,
        layerIds: ['pois_bicycle_parking'],
        poiType: 'parking',
        gpxSym: 'Parking Area',
        gpxType: 'bike_parking',
      },
      {
        id: 'bicycle_repair',
        label: 'Bicycle repair',
        icon: Wrench,
        layerIds: ['pois_bicycle_repair_station'],
        poiType: 'generic',
        gpxSym: 'Bike Trail',
        gpxType: 'bike_shop',
      },
    ],
  },
  {
    id: 'transport',
    label: 'Public transport',
    categories: [
      {
        id: 'airport',
        label: 'Airports & airfields',
        icon: Plane,
        layerIds: ['pois_aerodrome', 'pois_airport', 'pois_airfield', 'pois_helipad'],
        poiType: 'airport',
        gpxSym: 'Airport',
        gpxType: 'airport',
      },
      {
        id: 'station',
        label: 'Train stations',
        icon: TrainFront,
        layerIds: ['pois_station'],
        poiType: 'railway',
        gpxSym: 'Ground Transportation',
        gpxType: 'transit',
      },
      {
        id: 'bus_stop',
        label: 'Bus stops',
        icon: Bus,
        layerIds: ['pois_bus_stop'],
        poiType: 'bus',
        gpxSym: 'Ground Transportation',
        gpxType: 'transit',
      },
      {
        id: 'ferry',
        label: 'Ferry terminals',
        icon: Ship,
        layerIds: ['pois_ferry_terminal'],
        poiType: 'ferry',
        gpxSym: 'Ferry',
        gpxType: 'ferry',
      },
    ],
  },
]

/** Flat list of every browse category. */
export const POI_BROWSE_CATEGORIES: PoiBrowseCategory[] = POI_BROWSE_GROUPS.flatMap(
  (group) => group.categories,
)

/** Every basemap layer id referenced by the browser (used for click hit-testing). */
export const POI_BROWSE_LAYER_IDS: string[] = Array.from(
  new Set(POI_BROWSE_CATEGORIES.flatMap((category) => category.layerIds)),
)

/**
 * The basemap `pois` vector-tile features carry a `kind` property (e.g. "restaurant", "alpine_hut")
 * that equals the `pois_<kind>` layer suffix. We render the browser's POIs straight from that source
 * filtered by `kind`, so the marker layer works from the same offline tiles.
 */
export const kindsForCategory = (category: PoiBrowseCategory): string[] =>
  category.layerIds.map((layerId) => layerId.replace(/^pois_/, ''))

/** Every POI `kind` the browser knows about. */
export const POI_BROWSE_KINDS: string[] = Array.from(
  new Set(POI_BROWSE_CATEGORIES.flatMap((category) => kindsForCategory(category))),
)

const categoryForKind = (kind: string): PoiBrowseCategory | undefined =>
  POI_BROWSE_CATEGORIES.find((category) => kindsForCategory(category).includes(kind))

/** Map a clicked basemap POI layer id back to its RouteCamp POI type. */
export function poiTypeForLayerId(layerId: string): string {
  const match = POI_BROWSE_CATEGORIES.find((category) => category.layerIds.includes(layerId))
  return match?.poiType ?? 'generic'
}

/** Human label for a clicked basemap POI layer id (fallback when the feature has no name). */
export function poiLabelForLayerId(layerId: string): string {
  const match = POI_BROWSE_CATEGORIES.find((category) => category.layerIds.includes(layerId))
  return match?.label.replace(/s$/, '') ?? 'Point of interest'
}

/** RouteCamp POI type for a basemap `kind` (used when adding the POI to a collection). */
export function poiTypeForKind(kind: string): string {
  return categoryForKind(kind)?.poiType ?? 'generic'
}

/** Singular human label for a basemap `kind` (fallback when the feature has no name). */
export function poiLabelForKind(kind: string): string {
  return categoryForKind(kind)?.label.replace(/s$/, '') ?? 'Point of interest'
}

/** Checklist/marker icon for a basemap `kind`. */
export function iconForKind(kind: string): Component | undefined {
  return categoryForKind(kind)?.icon
}

/** GPX `<sym>` / `<type>` for a basemap `kind`, for portable export. */
export function gpxSymbolForKind(kind: string): { sym: string; type: string } {
  const category = categoryForKind(kind)
  return { sym: category?.gpxSym ?? 'Waypoint', type: category?.gpxType ?? 'generic' }
}
